import { bind } from 'bind-event-listener';

import { type PollingOptions, type PollingState, PollingTimeoutError } from './types';

/**
 * Generic polling utility function, polling will halt on the first result that satisfies the `shouldContinue` predicate
 * or when the polling duration exceeds the `maxDuration`.
 * Polling will also stop on the first error thrown by the `fn` function.
 * @param fn
 * @param shouldContinue
 * @param options
 * @returns
 */
export async function poll<T>(
	fn: () => Promise<T>,
	shouldContinue: (result: T) => boolean,
	options: PollingOptions,
): Promise<T> {
	if (options.interval < 0 || options.maxDuration < 0) {
		throw new Error('Polling options must be positive');
	}

	const initialState: PollingState = {
		startTime: Date.now(),
		attempts: 0,
	};

	const checkTimeout = (state: PollingState) => {
		if (options.signal?.aborted) {
			throw new Error('Polling aborted');
		}
		if (Date.now() - state.startTime >= options.maxDuration) {
			throw new PollingTimeoutError(options.maxDuration);
		}
	};

	async function _poll(state: PollingState): Promise<T> {
		checkTimeout(state);

		const result = await withAbort(fn(), options.signal);

		if (!shouldContinue(result)) {
			return result;
		}

		await new Promise((resolve) => setTimeout(resolve, options.interval));

		return _poll({
			...state,
			attempts: state.attempts + 1,
		});
	}

	if (options.initialDelay > 0) {
		await new Promise((resolve) => setTimeout(resolve, options.initialDelay));
	}

	return _poll(initialState);
}

function withAbort<T>(promise: Promise<T>, signal: AbortSignal): Promise<T> {
	return new Promise<T>((resolve, reject) => {
		if (signal.aborted) {
			reject(new Error('The operation was aborted'));
			return;
		}

		const abortHandler = () => reject(new Error('The operation was aborted'));
		const unbind = bind(signal, {
			type: 'abort',
			listener: abortHandler,
			options: { once: true },
		});

		promise
			.finally(() => unbind())
			.then(resolve, reject)
			.catch(reject);
	});
}
